import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { validateTeamLogo, TEAM_LOGO_ACCEPT } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import LeaguePlayerPicker from './LeaguePlayerPicker';
import CountrySelect from './CountrySelect';
import './Leagues.css';

// Team modes and their active roster size. The mode labels (2v2 Wingman / 5v5
// Competitive) are English in-source, but stored as keys resolved via t() at
// render since this module-level constant can't call hooks.
const TEAM_MODES = [
  { key: 'wingman', labelKey: 'leagues.createModeWingman', size: 2 },
  { key: 'competitive', labelKey: 'leagues.createModeComp', size: 5 },
];

/**
 * Authenticated form to create a persistent league team: name, tag, mode, an
 * optional emblem, and an initial roster picked from the guild's players. The
 * captain is chosen among the selected players. On success it creates the team,
 * adds the remaining members, uploads the logo, and opens the team page.
 */
export default function CreateLeagueTeam() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getAuthHeaders, isLoggedIn } = useAuth();
  const { guildId } = usePermissions();

  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [color, setColor] = useState('#ff6b00');
  const [description, setDescription] = useState('');
  const [region, setRegion] = useState('');
  const [mode, setMode] = useState('wingman');
  const [selected, setSelected] = useState([]);
  const [captainId, setCaptainId] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const size = TEAM_MODES.find((m) => m.key === mode)?.size || 5;

  const togglePlayer = (player) => {
    setSelected((prev) => {
      const exists = prev.some((p) => p.player_id === player.player_id);
      if (exists) {
        if (captainId === player.player_id) setCaptainId(null);
        return prev.filter((p) => p.player_id !== player.player_id);
      }
      if (prev.length >= size) return prev;
      if (captainId === null) setCaptainId(player.player_id);
      return [...prev, player];
    });
  };

  const onLogoChange = (e) => {
    const file = e.target.files?.[0] || null;
    const logoError = validateTeamLogo(file);
    if (logoError) {
      setError(logoError);
      setLogoFile(null);
      setLogoPreview(null);
      e.target.value = '';
      return;
    }
    setError('');
    setLogoFile(file);
    setLogoPreview(file ? URL.createObjectURL(file) : null);
  };

  const playerName = (p) => p.steam?.username || p.username || p.player_id;

  const submit = async () => {
    setError('');
    if (!name.trim()) return setError(t('leagues.teamNameRequired'));
    if (!captainId) return setError(t('leagues.captainRequired'));

    setSubmitting(true);
    try {
      const auth = getAuthHeaders();
      const jsonHeaders = { ...auth, 'Content-Type': 'application/json' };

      const res = await fetch(`${API_URL}/api/leagues/teams`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({
          name: name.trim(), tag: tag.trim(), color, description: description.trim(),
          region: region.trim(), game_mode: mode, captain_player_id: captainId,
        }),
      });
      const team = await res.json();
      if (!res.ok) throw new Error(team.error || t('leagues.createTeamFailed'));

      for (const p of selected) {
        if (p.player_id === captainId) continue;
        await fetch(`${API_URL}/api/leagues/teams/${team.id}/members`, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({ player_id: p.player_id }),
        });
      }

      if (logoFile) {
        const fd = new FormData();
        fd.append('logo', logoFile);
        await fetch(`${API_URL}/api/leagues/teams/${team.id}/logo`, {
          method: 'POST',
          headers: auth, // no Content-Type → browser sets multipart boundary
          body: fd,
        });
      }

      navigate(`/leagues/teams/${team.id}`);
    } catch (e) {
      setError(e.message || t('auth.genericError'));
      setSubmitting(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="league-page">
        <div className="league-empty">{t('leagues.loginToCreateTeam')}</div>
      </div>
    );
  }

  return (
    <div className="league-page league-form-page">
      <div className="league-detail-header">
        <button className="league-back-btn" onClick={() => navigate('/leagues/teams')}>← {t('createMatch.teams')}</button>
        <h1 className="league-page-title">{t('titles.createTeam')}</h1>
      </div>

      {error && <div className="league-form-error">{error}</div>}

      <div className="league-form">
        <label className="form-field">
          <span className="form-label">{t('leagues.teamNameLabel')}</span>
          <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder={t('leagues.teamNamePlaceholder')} />
        </label>

        <div className="form-row">
          <label className="form-field">
            <span className="form-label">{t('leagues.tagLabel')}</span>
            <input className="form-input" value={tag} onChange={(e) => setTag(e.target.value)} maxLength={8} placeholder={t('leagues.tagPlaceholder')} />
            <span className="form-hint">{t('leagues.tagHint')}</span>
          </label>
          <label className="form-field">
            <span className="form-label">{t('leagues.countryOptional')}</span>
            <CountrySelect value={region} onChange={setRegion} />
          </label>
          <label className="form-field">
            <span className="form-label">{t('leagues.modeLabel')}</span>
            <select
              className="form-input"
              value={mode}
              onChange={(e) => { setMode(e.target.value); setSelected([]); setCaptainId(null); }}
            >
              {TEAM_MODES.map((m) => <option key={m.key} value={m.key}>{t(m.labelKey)}</option>)}
            </select>
          </label>
          <label className="form-field form-field-color">
            <span className="form-label">{t('leagues.teamColor')}</span>
            <input className="form-color" type="color" value={color} onChange={(e) => setColor(e.target.value)} />
          </label>
        </div>

        <label className="form-field">
          <span className="form-label">{t('leagues.mottoOptional')}</span>
          <textarea
            className="form-input form-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={280}
            rows={2}
            placeholder={t('leagues.mottoPlaceholder')}
          />
        </label>

        <label className="form-field">
          <span className="form-label">{t('leagues.emblemOptional')}</span>
          <div className="logo-upload">
            {logoPreview && <img className="logo-preview" src={logoPreview} alt="" />}
            <input type="file" accept={TEAM_LOGO_ACCEPT} onChange={onLogoChange} />
          </div>
        </label>

        <div className="form-field">
          <span className="form-label">{t('leagues.rosterPick', { count: selected.length, size })}</span>
          {selected.length > 0 && (
            <div className="roster-chips">
              {selected.map((p) => (
                <div key={p.player_id} className="roster-chip">
                  <button
                    type="button"
                    className={`chip-captain ${captainId === p.player_id ? 'is-captain' : ''}`}
                    title={t('leagues.makeCaptain')}
                    onClick={() => setCaptainId(p.player_id)}
                  >★</button>
                  <span className="chip-name">{playerName(p)}</span>
                  <button type="button" className="chip-remove" onClick={() => togglePlayer(p)}>×</button>
                </div>
              ))}
            </div>
          )}
          <LeaguePlayerPicker guildId={guildId} selected={selected} onToggle={togglePlayer} maxPlayers={size} />
        </div>

        <button className="form-submit" disabled={submitting} onClick={submit}>
          {submitting ? t('leagues.creating') : t('titles.createTeam')}
        </button>
      </div>
    </div>
  );
}
